import { Link } from 'react-router-dom';
import type { Recipe } from '../data/types';
import { MealBadge } from './MealBadge';
import { StarButton } from './StarButton';
import { PlanButton } from './PlanButton';
import { useRecipeScale } from '../lib/servings';
import { yieldUnit } from '../lib/scale';
import { labelFor } from '../data/filters';

export function RecipeCard({ recipe }: { recipe: Recipe }) {
  const scale = useRecipeScale(recipe);
  return (
    <div className="relative rounded-xl border border-line bg-card shadow-[0_1px_2px_rgba(43,38,34,0.05)] transition-shadow hover:shadow-[0_2px_10px_rgba(43,38,34,0.09)]">
      <Link
        to={`/recipe/${recipe.slug}`}
        className="block p-5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ink"
      >
        <div className="flex flex-wrap items-center gap-1.5 pr-10">
          <MealBadge meal={recipe.meal} />
          {recipe.category !== 'meal' && (
            <span className="font-sans text-[0.7rem] font-medium uppercase tracking-[0.08em] text-ink-soft">
              {labelFor('category', recipe.category)}
            </span>
          )}
          {recipe.cuisines.map((c) => (
            <span key={c} className="font-sans text-[0.7rem] font-medium uppercase tracking-[0.08em] text-ink-soft">
              · {labelFor('cuisines', c)}
            </span>
          ))}
        </div>
        <h2 className="mt-2.5 pr-10 font-serif text-2xl font-semibold leading-snug">
          {recipe.title}
        </h2>
        <p className="mt-2 line-clamp-2 font-serif text-[0.95rem] leading-relaxed text-ink-soft">
          {recipe.summary}
        </p>
        <dl className="mt-4 flex flex-wrap gap-x-5 gap-y-1 font-sans text-[0.8rem] text-ink-soft">
          <div className="flex gap-1.5">
            <dt className="font-medium text-ink">Makes</dt>
            <dd className={scale.isScaled ? 'font-semibold text-goal' : ''}>
              {scale.isScaled ? `${scale.current} ${yieldUnit(recipe.yield)} · scaled` : recipe.yield}
            </dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="font-medium text-ink">Time</dt>
            <dd>{recipe.totalTime}</dd>
          </div>
          <div className="flex gap-1.5">
            <dt className="font-medium text-ink">Freezer</dt>
            <dd>{recipe.freezerLife}</dd>
          </div>
        </dl>
      </Link>
      <div className="absolute right-2.5 top-2.5">
        <StarButton slug={recipe.slug} />
      </div>
      <div className="border-t border-line/60 px-5 py-2.5">
        <PlanButton slug={recipe.slug} />
      </div>
    </div>
  );
}
